import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * Fetches the signed-in user's profile photo from Microsoft Graph (same
 * image used in Teams, Outlook, etc.).
 *
 * Requires the `user.read` scope which is already configured in the
 * MSAL interceptor's protectedResourceMap.
 *
 * A 404 is normal — it just means the user hasn't uploaded a photo.
 * In that case the component falls back to showing initials.
 */
@Injectable({ providedIn: 'root' })
export class UserAvatarService {
  private readonly avatarUrl$ = new BehaviorSubject<string | null>(null);

  /** Observable that emits the object-URL of the user's avatar, or null. */
  readonly avatar$: Observable<string | null> = this.avatarUrl$.asObservable();

  constructor(private readonly http: HttpClient) {}

  /**
   * Call once at startup (e.g. from UserFacade or AppComponent).
   * Tries the 96×96 size first (cheaper), then falls back to the full photo.
   * A 404 from either means the user has no photo → initials are shown.
   */
  loadAvatar(): void {
    this.http
      .get('https://graph.microsoft.com/v1.0/me/photos/96x96/$value', { responseType: 'blob' })
      .pipe(
        catchError(() =>
          // 96×96 not available, try the default size
          this.http.get('https://graph.microsoft.com/v1.0/me/photo/$value', { responseType: 'blob' }).pipe(
            catchError(() =>
              // Beta endpoint sometimes has the photo sooner after upload
              this.http.get('https://graph.microsoft.com/beta/me/photo/$value', { responseType: 'blob' }),
            ),
          ),
        ),
        catchError(() => of(null)),
      )
      .subscribe((blob) => {
        if (blob && blob.size > 0) {
          const url = URL.createObjectURL(blob);
          this.avatarUrl$.next(url);
          console.log('[UserAvatar] Profile photo loaded from Microsoft Graph');
        } else {
          this.avatarUrl$.next(null);
          console.log('[UserAvatar] No profile photo found — using initials');
        }
      });
  }
}
